import { type IconName, Row, Transition } from "@mdotm/shadcn/legacy/components";
import { type DataAttributesProps, IconButton, type IconButtonAsyncFn } from "@mdotm/shadcn/mdotm/components";
import { useMouseOver } from "@mdotm/shadcn/legacy/headless";
import { overrideClassList } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { type MouseEventHandler, type ReactNode, type Ref, useMemo, useState } from "react";

export type OverlayActionsOption = {
	icon: IconName;
	disabled?: boolean;
	iconColor?: string;
	innerRef?: Ref<HTMLButtonElement>;
} & (
	| ({ onClick?: MouseEventHandler; onClickAsync?: undefined } & DataAttributesProps)
	| ({
			onClick?: undefined;
			// `IconButton`'s signature; the legacy abort signal it drops was never read.
			onClickAsync?: IconButtonAsyncFn;
	  } & DataAttributesProps)
);
export type OverlayActionsProps<Trigger extends HTMLElement | SVGElement> = {
	disabled?: boolean;
	actions?: Array<OverlayActionsOption | null>;
	triggerEl: Trigger | null;
} & (
	| {
			mode?: "hover";
	  }
	| {
			mode?: "manual";
			open: boolean;
	  }
);

function OverlayActions<Trigger extends HTMLElement | SVGElement>(
	props: OverlayActionsProps<Trigger>,
): React.JSX.Element {
	const { triggerEl, actions, disabled } = props;
	const normalizedActions = useMemo(() => actions?.filter((action) => action != null), [actions]);

	const [actionEl, setActionEl] = useState<HTMLElement | null>(null);
	const isMouseOver = useMouseOver({
		elements: useMemo(() => [triggerEl, actionEl], [triggerEl, actionEl]),
		disabled: !normalizedActions?.length || disabled || props.mode === "manual",
	});

	return (
		<Transition
			in={props.mode === "manual" ? props.open : isMouseOver}
			className="absolute right-2 top-1/2 -translate-y-1/2 transition-opacity left-auto bottom-auto"
			enterFromClassName="opacity-0 pointer-events-none"
			enterToClassName="opacity-100"
		>
			{({ className }) => (
				<Row
					minSize="0"
					flexGrow={0}
					innerRef={setActionEl}
					className={overrideClassList(
						"shadow-[0px_8px_16px_rgba(42,48,60,0.12)] rounded-sm bg-white flex-1 h-8",
						className,
					)}
				>
					<ForEach collection={normalizedActions ?? []}>
						{(item) =>
							item.onClickAsync ? (
								<IconButton
									{...item}
									icon={item.icon}
									onClickAsync={async (e, opts) => {
										e.stopPropagation();
										await item.onClickAsync!(e, opts);
									}}
									disabled={item.disabled}
									size={14}
									innerRef={item.innerRef}
								/>
							) : item.onClick ? (
								<IconButton
									{...item}
									icon={item.icon}
									onClick={(e) => {
										e.stopPropagation();
										item.onClick!(e);
									}}
									disabled={item.disabled}
									size={14}
									innerRef={item.innerRef}
								/>
							) : null
						}
					</ForEach>
				</Row>
			)}
		</Transition>
	);
}

export default OverlayActions;

export function WithOverlayActions({
	children,
	...forward
}: Omit<OverlayActionsProps<HTMLDivElement>, "triggerEl" | "mode"> & {
	children: ReactNode;
}): React.JSX.Element {
	const [triggerEl, setTriggerEl] = useState<HTMLDivElement | null>(null);

	return (
		<div ref={setTriggerEl} className="relative z-0">
			<div className="relative">{children}</div>
			<OverlayActions {...forward} mode="hover" triggerEl={triggerEl} />
		</div>
	);
}
